import { BrandLogo } from "@/components/BrandLogo";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { homeFor, useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1708807472445-d33589e6b090?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      nav(homeFor(u), { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden lg:block">
        <img src={HERO} alt="Centro de monitoreo" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Tracker &amp; Security Systems MX</p>
          <h2 className="mt-3 max-w-md font-display text-4xl font-bold leading-tight">Cada servicio, documentado de principio a fin.</h2>
        </div>
      </div>
      <div className="grid-bg flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="fade-up w-full max-w-sm space-y-8" data-testid="login-form">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-3 rounded-xl bg-white px-4 py-3">
              <BrandLogo alt="Tracker & Security Systems MX" className="h-12 w-auto" testid="login-logo" />
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <span className="font-display text-2xl font-extrabold tracking-tight">TSSMX <span className="text-primary">OS</span></span>
            </div>
            <p className="text-sm text-muted-foreground">Órdenes de Servicio · Videovigilancia · Control de Acceso · Alarma</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Correo</Label>
              <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 text-base" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Contraseña</Label>
              <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 text-base" data-testid="login-password-input" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="h-12 w-full text-base font-semibold" data-testid="login-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Iniciar sesión
          </Button>
          <p className="text-xs text-muted-foreground">Las cuentas son creadas por el administrador de TSSMX.</p>
        </form>
      </div>
    </div>
  );
}
